'use client';

import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { useTranslations } from 'next-intl';
import {
  BodyDefault,
  Button,
  ConfirmationDialog,
  Field,
  LabelDefault,
  RadioGroup,
  Select,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Target03Icon } from '@terra/ui-icons';
import {
  SignalStatusSchema,
  type SignalStatusType,
} from '@terra/shared-schemas-orchestrator';
import {
  FINDING_DELETE_SIGNAL_ACTION,
  FINDING_DELETE_SIGNAL_STATUS_OPTIONS,
  buildFindingDeleteSignalDisposition,
  isFindingDeleteConfirmDisabled,
  type FindingDeleteRelatedSignal,
  type FindingDeleteSignalAction,
  type FindingDeleteSignalDisposition,
} from './findingDeleteSignalDisposition';

type FindingDeleteConfirmationDialogProps = {
  isOpen: boolean;
  isBulk: boolean;
  relatedSignal: FindingDeleteRelatedSignal | null;
  loading?: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  onOpenRelatedSignal: (signalId: string) => void;
  onConfirm: (
    disposition: FindingDeleteSignalDisposition | null,
  ) => void | Promise<void>;
};

type FindingDeleteRelatedSignalFieldsProps = {
  relatedSignal: FindingDeleteRelatedSignal;
  dispositionRef: RefObject<FindingDeleteSignalDisposition | null>;
  onOpenRelatedSignal: (signalId: string) => void;
  onConfirmDisabledChange: (disabled: boolean) => void;
};

const FindingDeleteRelatedSignalFields = ({
  relatedSignal,
  dispositionRef,
  onOpenRelatedSignal,
  onConfirmDisabledChange,
}: FindingDeleteRelatedSignalFieldsProps) => {
  const t = useTranslations('findingsTab.deleteConfirmation');
  const tSignalStatus = useTranslations(
    'designSystem.signals.lifecycle.status',
  );
  const [signalAction, setSignalAction] = useState<FindingDeleteSignalAction>(
    FINDING_DELETE_SIGNAL_ACTION.KEEP,
  );
  const [selectedStatus, setSelectedStatus] = useState<SignalStatusType | null>(
    null,
  );
  const statusOptions = useMemo(
    () =>
      FINDING_DELETE_SIGNAL_STATUS_OPTIONS.map((status) => ({
        label: tSignalStatus(status),
        value: status,
      })),
    [tSignalStatus],
  );

  const confirmDisabled = isFindingDeleteConfirmDisabled({
    hasRelatedSignal: true,
    action: signalAction,
    selectedStatus,
  });

  dispositionRef.current = buildFindingDeleteSignalDisposition({
    action: signalAction,
    selectedStatus,
  });

  useLayoutEffect(() => {
    onConfirmDisabledChange(confirmDisabled);
  }, [confirmDisabled, onConfirmDisabledChange]);

  return (
    <VStack gap="4" alignItems="stretch" w="full">
      <VStack gap="1" alignItems="flex-start" w="full">
        <BodyDefault color="fg.muted">
          {t('relatedSignalDescription')}
        </BodyDefault>
        <Button
          variant="link"
          size="sm"
          maxW="full"
          justifyContent="flex-start"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onOpenRelatedSignal(relatedSignal.id);
          }}
          transition="opacity 0.2s ease-in-out"
          _hover={{
            opacity: 0.8,
          }}
        >
          <HStack gap="2" alignItems="center" minW={0} maxW="full">
            <Target03Icon size={16} style={{ flexShrink: 0 }} />
            <Box as="span" truncate minW={0}>
              {relatedSignal.title?.trim() || relatedSignal.id}
            </Box>
          </HStack>
        </Button>
      </VStack>

      <VStack gap="2" alignItems="stretch" w="full">
        <RadioGroup.Root
          w="full"
          value={signalAction}
          onValueChange={(details) => {
            if (!details.value) {
              return;
            }

            const nextAction = details.value as FindingDeleteSignalAction;

            setSignalAction(nextAction);

            if (nextAction !== FINDING_DELETE_SIGNAL_ACTION.CHANGE_STATUS) {
              setSelectedStatus(null);
            }
          }}
        >
          <VStack gap="2" alignItems="stretch" w="full">
            <RadioGroup.Item value={FINDING_DELETE_SIGNAL_ACTION.KEEP} w="full">
              <RadioGroup.ItemControl flexShrink={0} />
              <RadioGroup.ItemText>
                <BodyDefault>{t('keepStatus')}</BodyDefault>
              </RadioGroup.ItemText>
              <RadioGroup.ItemHiddenInput />
            </RadioGroup.Item>
            <RadioGroup.Item
              value={FINDING_DELETE_SIGNAL_ACTION.CHANGE_STATUS}
              w="full"
            >
              <RadioGroup.ItemControl flexShrink={0} />
              <RadioGroup.ItemText>
                <BodyDefault>{t('changeStatus')}</BodyDefault>
              </RadioGroup.ItemText>
              <RadioGroup.ItemHiddenInput />
            </RadioGroup.Item>
            <RadioGroup.Item
              value={FINDING_DELETE_SIGNAL_ACTION.DELETE}
              w="full"
            >
              <RadioGroup.ItemControl flexShrink={0} />
              <RadioGroup.ItemText>
                <BodyDefault>{t('deleteSignal')}</BodyDefault>
              </RadioGroup.ItemText>
              <RadioGroup.ItemHiddenInput />
            </RadioGroup.Item>
          </VStack>
        </RadioGroup.Root>
      </VStack>

      {signalAction === FINDING_DELETE_SIGNAL_ACTION.CHANGE_STATUS ? (
        <Field.Root w="full">
          <Field.Label>
            <LabelDefault>{t('statusLabel')}</LabelDefault>
          </Field.Label>
          <Select.SelectWithOptions
            options={statusOptions}
            value={selectedStatus ? [selectedStatus] : []}
            onValueChange={(nextStatus) => {
              if (!nextStatus) {
                setSelectedStatus(null);

                return;
              }

              const parsed = SignalStatusSchema.safeParse(nextStatus);

              if (parsed.success) {
                setSelectedStatus(parsed.data);
              }
            }}
            placeholder={t('statusPlaceholder')}
            borderRadius="md"
            spaceBetween
          />
        </Field.Root>
      ) : null}
    </VStack>
  );
};

export function FindingDeleteConfirmationDialog({
  isOpen,
  isBulk,
  relatedSignal,
  loading,
  onOpenChange,
  onOpenRelatedSignal,
  onConfirm,
}: FindingDeleteConfirmationDialogProps) {
  const t = useTranslations('findingsTab.deleteConfirmation');
  const tCommon = useTranslations('common');
  const sessionRef = useRef({ isBulk, relatedSignal });
  const dispositionRef = useRef<FindingDeleteSignalDisposition | null>(null);
  const [confirmDisabled, setConfirmDisabled] = useState(false);

  if (isOpen) {
    sessionRef.current = { isBulk, relatedSignal };
  }

  const sessionIsBulk = sessionRef.current.isBulk;
  const sessionRelatedSignal = sessionRef.current.relatedSignal;
  const hasRelatedSignal = !!sessionRelatedSignal && !sessionIsBulk;

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onConfirm={() => {
        if (!hasRelatedSignal) {
          return onConfirm(null);
        }

        return onConfirm(dispositionRef.current);
      }}
      loading={loading}
      variant="destructive"
      title={t('title', { isBulk: String(sessionIsBulk) })}
      descriptionContent={
        <BodyDefault color="fg.muted">
          {t('description', { isBulk: String(sessionIsBulk) })}
        </BodyDefault>
      }
      confirmText={tCommon('delete')}
      cancelText={tCommon('cancel')}
      confirmDisabled={hasRelatedSignal ? confirmDisabled : false}
      maxWidth={hasRelatedSignal ? '480px' : '400px'}
    >
      {hasRelatedSignal && sessionRelatedSignal ? (
        <FindingDeleteRelatedSignalFields
          relatedSignal={sessionRelatedSignal}
          dispositionRef={dispositionRef}
          onOpenRelatedSignal={onOpenRelatedSignal}
          onConfirmDisabledChange={setConfirmDisabled}
        />
      ) : null}
    </ConfirmationDialog>
  );
}
